import React from 'react';
import styled from 'styled-components';
import ResponsiveBackButton from '../button/ResponsiveBackButton';

interface ResponsivePageHeaderProps {
  onBackClick: () => void;
  backButtonText: string;
  title: string;
  description?: string;
  variant?: 'primary' | 'secondary' | 'minimal';
  className?: string;
}

const HeaderContainer = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  
  /* Responsive layout to prevent overlap */
  @media (min-width: 1200px) {
    padding: 30px;
    gap: 20px;
  }
  
  @media (min-width: 768px) and (max-width: 1199px) {
    padding: 25px;
    gap: 18px;
  }
  
  @media (min-width: 480px) and (max-width: 767px) {
    padding: 20px;
    gap: 15px;
  }
  
  @media (max-width: 479px) {
    padding: 15px;
    gap: 12px;
  }
  
  /* Ensure back button and title don't overlap */
  @media (max-width: 600px) {
    text-align: center;
    
    /* Stack elements vertically on narrow screens */
    > * {
      width: 100%;
      max-width: 100%;
    }
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  width: 100%;
  
  /* Responsive font sizing */
  @media (min-width: 1200px) {
    font-size: 32px;
  }
  
  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 28px;
  }
  
  @media (min-width: 480px) and (max-width: 767px) {
    font-size: 24px;
  }
  
  @media (max-width: 479px) {
    font-size: 20px;
  }
  
  /* Ensure text doesn't overflow on very narrow screens */
  @media (max-width: 400px) {
    font-size: 18px;
    word-wrap: break-word;
    hyphens: auto;
  }
`;

const Description = styled.p`
  margin: 10px 0 0 0;
  opacity: 0.9;
  text-align: center;
  width: 100%;
  max-width: 800px;
  
  /* Responsive font sizing */
  @media (min-width: 1200px) {
    font-size: 16px;
  }
  
  @media (min-width: 768px) and (max-width: 1199px) {
    font-size: 15px;
  }
  
  @media (min-width: 480px) and (max-width: 767px) {
    font-size: 14px;
  }
  
  @media (max-width: 479px) {
    font-size: 13px;
  }
  
  /* Ensure text doesn't overflow on very narrow screens */
  @media (max-width: 400px) {
    font-size: 12px;
    word-wrap: break-word;
    hyphens: auto;
  }
`;

const BackButtonWrapper = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  
  /* Responsive positioning */
  @media (min-width: 1200px) {
    top: 25px;
    left: 25px;
  }
  
  @media (min-width: 768px) and (max-width: 1199px) {
    top: 22px;
    left: 22px;
  }
  
  @media (min-width: 480px) and (max-width: 767px) {
    top: 18px;
    left: 18px;
  }
  
  @media (max-width: 479px) {
    top: 15px;
    left: 15px;
  }
  
  /* On very narrow screens, adjust position to prevent overlap */
  @media (max-width: 400px) {
    top: 12px;
    left: 12px;
  }
`;

const ResponsivePageHeader: React.FC<ResponsivePageHeaderProps> = ({
  onBackClick,
  backButtonText,
  title,
  description,
  variant = 'secondary',
  className
}) => {
  return (
    <HeaderContainer className={className}>
      <BackButtonWrapper>
        <ResponsiveBackButton onClick={onBackClick} variant={variant}>
          {backButtonText}
        </ResponsiveBackButton>
      </BackButtonWrapper>
      
      <Title>{title}</Title>
      
      {description && <Description>{description}</Description>}
    </HeaderContainer>
  );
};

export default ResponsivePageHeader;
